/**
 * Technician attribute form — dropdowns for Tier, Clearance, Location.
 * Tier dropdown items are colour-coded to match the StatusBadge tier colours.
 * Reused by Add Technician dialog and Edit Attributes dialog.
 */

import type { TechnicianAttributeUpdate } from "@/types";

const TIER_OPTIONS = [
  { value: "Tier-0", label: "Tier-0 — Break-Glass", color: "text-sc-danger" },
  { value: "Tier-1", label: "Tier-1 — Senior Tech", color: "text-amber-600 dark:text-amber-400" },
  { value: "Tier-2", label: "Tier-2 — Standard Tech", color: "text-blue-600 dark:text-blue-400" },
  { value: "Tier-3", label: "Tier-3 — Junior / Limited", color: "text-purple-600 dark:text-purple-400" },
  { value: "Tier-4", label: "Tier-4 — Read-Only / Monitoring", color: "text-sc-success" },
] as const;

const CLEARANCE_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Basic", label: "Basic" },
  { value: "NV1", label: "NV1" },
  { value: "NV2", label: "NV2" },
  { value: "PV", label: "PV (Positive Vetting)" },
] as const;

const LOCATION_OPTIONS = [
  { value: "AU", label: "Australia" },
  { value: "CA", label: "Canada" },
  { value: "EU", label: "Europe" },
  { value: "NZ", label: "New Zealand" },
  { value: "PH", label: "Philippines" },
  { value: "US", label: "USA" },
] as const;

interface TechnicianAttributeFormProps {
  readonly attributes: TechnicianAttributeUpdate;
  readonly onChange: (attrs: TechnicianAttributeUpdate) => void;
}

export function TechnicianAttributeForm({
  attributes,
  onChange,
}: TechnicianAttributeFormProps) {
  return (
    <div className="space-y-4">
      {/* Tier */}
      <div>
        <label className="text-label text-sc-text-muted uppercase tracking-widest block mb-1.5">
          Technician Tier
        </label>
        <select
          value={attributes.technicianTier ?? ""}
          onChange={(e) =>
            onChange({
              ...attributes,
              technicianTier: e.target.value as TechnicianAttributeUpdate["technicianTier"],
            })
          }
          className="h-10 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
        >
          <option value="">Select tier...</option>
          {TIER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {attributes.technicianTier && (
          <TierIndicator tier={attributes.technicianTier} />
        )}
      </div>

      {/* Security Clearance */}
      <div>
        <label className="text-label text-sc-text-muted uppercase tracking-widest block mb-1.5">
          Security Clearance
        </label>
        <select
          value={attributes.securityClearance ?? ""}
          onChange={(e) =>
            onChange({
              ...attributes,
              securityClearance: (e.target.value || null) as TechnicianAttributeUpdate["securityClearance"],
            })
          }
          className="h-10 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
        >
          {CLEARANCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Work Location */}
      <div>
        <label className="text-label text-sc-text-muted uppercase tracking-widest block mb-1.5">
          Work Location
        </label>
        <select
          value={attributes.workLocationCountry ?? ""}
          onChange={(e) =>
            onChange({
              ...attributes,
              workLocationCountry: (e.target.value || null) as TechnicianAttributeUpdate["workLocationCountry"],
            })
          }
          className="h-10 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
        >
          <option value="">Not set</option>
          {LOCATION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {/* Teams Specialist */}
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={attributes.teamsSpecialist ?? false}
            onChange={(e) =>
              onChange({
                ...attributes,
                teamsSpecialist: e.target.checked,
              })
            }
            className="h-4 w-4 rounded border-sc-border text-sc-primary focus:ring-sc-primary"
          />
          <span className="text-body-sm text-sc-text">
            Teams Specialist — enables Teams-specific GDAP activation
          </span>
        </label>
      </div>
    </div>
  );
}

function TierIndicator({ tier }: { readonly tier: string }) {
  const option = TIER_OPTIONS.find((t) => t.value === tier);
  if (!option) return null;

  return (
    <div className="flex items-center gap-2 mt-1.5">
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full ${tierDotColor(tier)}`}
      />
      <span className={`text-label ${option.color}`}>{option.label}</span>
    </div>
  );
}

function tierDotColor(tier: string): string {
  switch (tier) {
    case "Tier-0":
      return "bg-sc-danger";
    case "Tier-1":
      return "bg-amber-500";
    case "Tier-2":
      return "bg-blue-500";
    case "Tier-3":
      return "bg-purple-500";
    case "Tier-4":
      return "bg-sc-success";
    default:
      return "bg-gray-400";
  }
}
